import { createContext } from 'react';
import type { User } from '@/configs/apis/user';
import { userApis, userDB } from '@/configs/apis/user';
import Paths from '@/configs/router/paths';
import { chatApis } from '@/configs/apis/chat';
import { groupDB } from '@/configs/apis/groups';
import type { UserResponse } from '@/pages/login/interface';
import { exportApis } from '@/scripts/utils/devUtils';
import { serverTime } from '../chat/scripts/hooks/useSystemTime/utils';
import { initFileTransferService } from '../chat/scripts/hooks/useFileMessages/file-sdk';
import { useUserSettings } from './hooks/useUserSettings';

export interface SessionContextState {
  user: User | null;
}

export type SessionContextValue = ReturnType<typeof useSessionContextValue>;

export const sessionContext = createContext<SessionContextValue>({} as never);

export function getInitState(
  defaultState?: Partial<SessionContextState>
): SessionContextState {
  const state: SessionContextState = {
    user: null,
  };

  if (defaultState) {
    for (const k in defaultState) {
      const v = defaultState[k as keyof SessionContextState];
      if (v !== undefined) {
        (state as any)[k] = v;
      }
    }
  }

  return state;
}

export function useSessionContextValue() {
  const [session, setSession] = useState(getInitState());
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();

  const userSettings = useUserSettings();
  const { updateUserSettingsContent } = userSettings;

  const logout = useCallback(async () => {
    setLoading(true);
    try {
      await userApis.logout();
      navigate(Paths.LOGIN);
      setSession(getInitState());
    } finally {
      setLoading(false);
    }
  }, [navigate]);

  const initSession = useCallback(async () => {
    setLoading(true);
    try {
      if (userApis.isLogin()) {
        const userId = userApis.getUserId();

        await Promise.allSettled([
          userDB.init(userId),
          groupDB.initCrypto(userId),
          serverTime.initPromise,
          initFileTransferService(),
          updateUserSettingsContent(),
        ]);

        const user = await userApis.getUser();

        chatApis.deleteAllExpiredMessages(serverTime.time);

        serverTime.addEventListener('sync', () => {
          chatApis.deleteAllExpiredMessages(serverTime.time);
        });

        setSession((prev) => ({
          ...prev,
          user,
        }));
      }
    } finally {
      setLoading(false);
    }
  }, [updateUserSettingsContent]);

  const login = useCallback(
    async (data: UserResponse) => {
      setLoading(true);
      try {
        await userDB.init(data.userId);
        await userApis.setUser(data);
        userApis.setToken(data.token);
        userApis.setUserId(data.userId);
        await initSession();
      } finally {
        setLoading(false);
      }
    },
    [initSession]
  );

  const value = useMemo(
    () => ({
      session,
      setSession,
      initSession,
      loading,
      setLoading,
      login,
      logout,
      userSettings,
    }),
    [session, initSession, loading, login, logout, userSettings]
  );

  useLayoutEffect(() => {
    initSession();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useLayoutEffect(() => {
    exportApis({ login });
  }, [login]);

  return value;
}

export function useSessionContext() {
  return useContext(sessionContext);
}
